import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
  withStyles
} from '@material-ui/core';
import { Pagination } from '@material-ui/lab';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import FilterListIcon from '@material-ui/icons/FilterList';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import DeleteIcon from '@material-ui/icons/Delete';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { getListAgencies } from 'src/features/agencySlice';
import { getListWarrantyTickets, deleteWarrantyTicket } from 'src/features/deviceSlice';
import CreateWarrantyTicketModal from './CreateWarrantyTicketModal';
import SwapSerialModal from './SwapSerialModal';

const StyledTableCell = withStyles(() => ({
  head: {
    backgroundColor: '#f8fafc',
    color: '#334155',
    fontWeight: 700,
    fontSize: 13,
    whiteSpace: 'nowrap',
    borderBottom: '2px solid #e2e8f0',
    verticalAlign: 'middle'
  },
  body: {
    fontSize: 13,
    whiteSpace: 'nowrap',
    color: '#334155',
    verticalAlign: 'middle'
  },
  root: { padding: '12px 14px' }
}))(TableCell);

const flattenAgencies = (agencies, parentName = '') => {
  const result = [];
  (agencies || []).forEach(agency => {
    result.push({ ...agency, parentName });
    if (agency.children?.length) {
      result.push(...flattenAgencies(agency.children, agency.name));
    }
  });
  return result;
};

const roleMap = {
  admin: 'Administrator',
  administrator: 'Administrator',
  agency: 'Đại lý',
  sale: 'Sale',
  customer_care: 'CSKH',
  technician: 'Kỹ thuật viên',
  accountant: 'Kế toán',
  lead_marketing: 'Lead Marketing',
  staff: 'Nhân viên',
  driver: 'Tài xế',
  enduser: 'Khách hàng'
};

const getCreatorInfo = (row, dataLogin, flatAgencies) => {
  const rawUser =
    row.performed_by_username ||
    row.created_by_username ||
    row.username ||
    row.creator?.username ||
    row.creator?.full_name ||
    row.user?.username ||
    '';

  if (!rawUser || rawUser === '-') {
    return { username: '-', role: '' };
  }

  // Tách username nếu chuỗi có số điện thoại dạng "vmake (0988669999)"
  const username = rawUser.includes('(')
    ? rawUser.split('(')[0].trim()
    : rawUser.trim();

  // Lấy role từ row nếu backend trả về
  const rawRole =
    row.performed_by_role ||
    row.creator_role ||
    row.created_by_role ||
    row.role_title ||
    row.role_name ||
    row.role ||
    (typeof row.creator?.role === 'string' ? row.creator.role : row.creator?.role?.title) ||
    (typeof row.user?.role === 'string' ? row.user.role : row.user?.role?.title) ||
    '';

  let role = '';
  if (rawRole) {
    const key = String(rawRole).toLowerCase();
    role = roleMap[key] || rawRole;
  } else if (username.toLowerCase() === 'admin' || username.toLowerCase() === 'administrator') {
    role = 'Administrator';
  } else if (dataLogin?.username && username.toLowerCase() === dataLogin.username.toLowerCase()) {
    const userRoleKey = (dataLogin?.role?.title || '').toLowerCase();
    role = roleMap[userRoleKey] || dataLogin?.role?.title || 'Admin';
  } else if (username.toLowerCase() === 'vmake') {
    role = 'Đại lý';
  } else {
    const isAgency = (flatAgencies || []).some(
      a =>
        a.name?.toLowerCase() === username.toLowerCase() ||
        a.agency_code?.toLowerCase() === username.toLowerCase() ||
        a.leader?.username?.toLowerCase() === username.toLowerCase()
    );
    if (isAgency) {
      role = 'Đại lý';
    }
  }

  return { username, role };
};

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(0),
    paddingRight: theme.spacing(0)
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    padding: '12px 16px',
    marginBottom: 16,
    boxShadow: 'none',
    border: '1px solid #e0e3ef',
    borderRadius: 8,
    backgroundColor: '#fff'
  },
  searchInput: {
    flex: '1 1 240px',
    minWidth: 220,
    maxWidth: 340,
    '& .MuiOutlinedInput-root': {
      borderRadius: 8
    }
  },
  filterSelect: {
    minWidth: 150,
    '& .MuiOutlinedInput-root': {
      borderRadius: 8
    },
    '& .MuiInputLabel-root': {
      fontSize: 13
    },
    '& .MuiOutlinedInput-input': {
      fontSize: 13,
      padding: '10.5px 14px'
    }
  },
  createBtn: {
    backgroundColor: '#D9042F',
    color: '#fff',
    fontWeight: 700,
    textTransform: 'none',
    height: 40,
    borderRadius: 8,
    boxShadow: 'none',
    marginLeft: 'auto',
    whiteSpace: 'nowrap',
    padding: '8px 16px',
    '&:hover': {
      backgroundColor: '#b70327'
    }
  },
  tableCard: {
    boxShadow: 'none',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: '#fff'
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#f8fafc !important',
      '& $stickyCol1, & $stickyCol2, & $stickyCol3': {
        backgroundColor: '#f8fafc !important'
      }
    }
  },
  stickyCol1: {
    position: 'sticky',
    left: 0,
    backgroundColor: '#ffffff !important',
    zIndex: 10
  },
  stickyCol2: {
    position: 'sticky',
    left: 140,
    backgroundColor: '#ffffff !important',
    zIndex: 10
  },
  stickyCol3: {
    position: 'sticky',
    left: 260,
    backgroundColor: '#ffffff !important',
    zIndex: 10
  },
  stickyHeader: {
    position: 'sticky',
    zIndex: 11,
    backgroundColor: '#f8fafc !important'
  },
  chipSuccess: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontWeight: 600,
    '& .MuiChip-icon': {
      color: '#2e7d32'
    }
  },
  chipWarning: {
    backgroundColor: '#fff8e1',
    color: '#b78103',
    fontWeight: 600,
    '& .MuiChip-icon': {
      color: '#b78103'
    }
  },
  chipCancel: {
    backgroundColor: '#ffebee',
    color: '#c62828',
    fontWeight: 600,
    '& .MuiChip-icon': {
      color: '#c62828'
    }
  }
}));

export default function WarrantyTicketsView() {
  const classes = useStyles();
  const dispatch = useDispatch();

  const isAdmin = useSelector(state => state.authSlice.isAdmin);
  const isCustomerCare = useSelector(state => state.authSlice.isCustomerCare);
  const isSale = useSelector(state => state.authSlice.isSale);
  const dataLogin = useSelector(state => state.authSlice?.dataLogin);
  const listAgencies = useSelector(state => state.agencySlice.listAgencies);

  // Phạm vi Global: admin hoặc user có agency_id = -1 / không gắn đại lý
  const userAgencyId = dataLogin?.agency_id ?? dataLogin?.agency?.id;
  const isGlobal = Boolean(
    isAdmin ||
    userAgencyId === undefined ||
    userAgencyId === null ||
    Number(userAgencyId) === -1 ||
    Number(userAgencyId) === 0
  );

  // Quyền lập phiếu: admin, customer_care, sale
  const canCreateTicket = Boolean(isAdmin || isCustomerCare || isSale);

  // Quyền sửa, xóa: admin, customer_care của admin (bắt buộc scope.isGlobal)
  const isGlobalCustomerCare = Boolean(isCustomerCare && isGlobal);
  const canEditOrDeleteTicket = Boolean(isAdmin || isGlobalCustomerCare);

  const flatAgencies = useMemo(
    () => (isAdmin ? flattenAgencies(listAgencies) : listAgencies || []),
    [isAdmin, listAgencies]
  );

  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState({
    page: 1,
    page_size: 20,
    serial: '',
    agency_id: '',
    status: '',
    from: '',
    to: ''
  });

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openSwapModal, setOpenSwapModal] = useState(false);
  const [selectedTicketForSwap, setSelectedTicketForSwap] = useState(null);

  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleActionMenuOpen = (event, ticket) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedTicket(ticket);
  };

  const handleActionMenuClose = () => {
    setActionAnchorEl(null);
  };

  const handleOpenSwapModal = ticket => {
    setSelectedTicketForSwap(ticket);
    setOpenSwapModal(true);
  };

  const handleOpenDeleteDialog = ticket => {
    setSelectedTicket(ticket);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTicket?.id) return;
    setDeleting(true);
    try {
      await dispatch(deleteWarrantyTicket(selectedTicket.id)).unwrap();
      setOpenDeleteDialog(false);
      setSelectedTicket(null);
      fetchTickets();
    } catch (err) {
      // Toast handled by thunk
    } finally {
      setDeleting(false);
    }
  };

  // Horizontal Drag Scroll State
  const scrollContainerRef = useRef(null);
  const [isDown, setIsDown] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isScrolled, setIsScrolled] = useState(false);

  const onMouseDown = e => {
    setIsDown(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };
  const onMouseLeave = () => setIsDown(false);
  const onMouseUp = () => setIsDown(false);
  const onMouseMove = e => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
    handleScroll();
  };

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      setIsScrolled(scrollContainerRef.current.scrollLeft > 0);
    }
  };

  useEffect(() => {
    dispatch(getListAgencies({ page_size: 1000 }));
  }, [dispatch]);

  const fetchTickets = async (params = filters) => {
    setLoading(true);
    try {
      const cleanParams = {
        page: params.page,
        page_size: params.page_size
      };
      if (params.serial?.trim()) cleanParams.serial = params.serial.trim();
      if (params.agency_id) cleanParams.agency_id = params.agency_id;
      if (params.status !== '') cleanParams.status = params.status;
      if (params.from) cleanParams.from = moment(params.from).startOf('day').toISOString();
      if (params.to) cleanParams.to = moment(params.to).endOf('day').toISOString();

      const res = await dispatch(getListWarrantyTickets(cleanParams)).unwrap();
      const payload = res?.payload || res;
      setTickets(payload?.data || []);
      setTotal(payload?.total || 0);
    } catch (err) {
      setTickets([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.page_size, filters.serial, filters.agency_id, filters.status, filters.from, filters.to]);

  const handleFilterClick = event => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleReset = () => {
    const defaultFilters = {
      page: 1,
      page_size: 20,
      serial: '',
      agency_id: '',
      status: '',
      from: '',
      to: ''
    };
    setFilters(defaultFilters);
  };

  const handlePageChange = (event, value) => {
    setFilters(prev => ({ ...prev, page: value }));
  };

  const handleCreateSuccess = () => {
    fetchTickets();
  };

  const activeFilterCount = [
    Boolean(filters.agency_id),
    Boolean(filters.status !== '')
  ].filter(Boolean).length;

  const hasActiveFilters = Boolean(
    filters.serial ||
    filters.agency_id ||
    filters.status !== '' ||
    filters.from ||
    filters.to
  );

  return (
    <Container maxWidth="xl" className={classes.root}>
      {/* 🔍 Filter & Action Toolbar (Chuẩn UI Quản trị bán hàng) */}
      <Card className={classes.toolbar}>
        <TextField
          id="search-warranty-serial"
          className={classes.searchInput}
          placeholder="Serial lỗi hoặc bảo hành..."
          variant="outlined"
          size="small"
          value={filters.serial}
          onChange={e => {
            setFilters({ ...filters, serial: e.target.value, page: 1 });
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" style={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
            endAdornment: filters.serial ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setFilters({ ...filters, serial: '', page: 1 });
                  }}
                >
                  <CloseIcon style={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />

        <TextField
          label="Từ ngày"
          type="date"
          placeholder="Từ ngày"
          variant="outlined"
          size="small"
          value={filters.from}
          onChange={e => {
            setFilters({ ...filters, from: e.target.value, page: 1 });
          }}
          className={classes.filterSelect}
          InputLabelProps={{
            shrink: true,
            style: {
              backgroundColor: 'white',
              padding: '0 4px',
              marginLeft: -4
            }
          }}
          InputProps={{
            endAdornment: filters.from ? (
              <InputAdornment
                position="end"
                style={{ position: 'absolute', right: 28 }}
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    setFilters({ ...filters, from: '', page: 1 });
                  }}
                  style={{ padding: 2 }}
                >
                  <CloseIcon style={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />

        <TextField
          label="Đến ngày"
          type="date"
          placeholder="Đến ngày"
          variant="outlined"
          size="small"
          value={filters.to}
          onChange={e => {
            setFilters({ ...filters, to: e.target.value, page: 1 });
          }}
          className={classes.filterSelect}
          InputLabelProps={{
            shrink: true,
            style: {
              backgroundColor: 'white',
              padding: '0 4px',
              marginLeft: -4
            }
          }}
          InputProps={{
            endAdornment: filters.to ? (
              <InputAdornment
                position="end"
                style={{ position: 'absolute', right: 28 }}
              >
                <IconButton
                  size="small"
                  onClick={() => {
                    setFilters({ ...filters, to: '', page: 1 });
                  }}
                  style={{ padding: 2 }}
                >
                  <CloseIcon style={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />

        <Button
          variant="outlined"
          size="small"
          onClick={handleFilterClick}
          startIcon={<FilterListIcon />}
          style={{
            height: 40,
            borderRadius: 8,
            textTransform: 'none',
            fontWeight: 600,
            color: activeFilterCount > 0 ? '#D9042F' : '#666',
            borderColor: activeFilterCount > 0 ? '#D9042F' : '#e0e3ef',
            backgroundColor: activeFilterCount > 0 ? '#fff1f0' : 'white'
          }}
        >
          Lọc {activeFilterCount > 0 ? `(${activeFilterCount})` : ''}
        </Button>

        <Popover
          open={Boolean(filterAnchorEl)}
          anchorEl={filterAnchorEl}
          onClose={handleFilterClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          PaperProps={{
            style: {
              padding: 20,
              width: 300,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
              borderRadius: 12,
              marginTop: 8,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
            }
          }}
        >
          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
            Bộ lọc bổ sung
          </Typography>

          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel shrink style={{ backgroundColor: 'white', padding: '0 4px', marginLeft: -4 }}>
              Đại lý / Kho
            </InputLabel>
            <Select
              value={filters.agency_id}
              onChange={e => {
                setFilters({ ...filters, agency_id: e.target.value, page: 1 });
              }}
            >
              <MenuItem value="">-- Tất cả đại lý --</MenuItem>
              {flatAgencies.map(ag => (
                <MenuItem key={ag.id} value={ag.id}>
                  {ag.parentName ? `${ag.parentName} ➔ ` : ''}{ag.name} ({ag.agency_code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" fullWidth>
            <InputLabel shrink style={{ backgroundColor: 'white', padding: '0 4px', marginLeft: -4 }}>
              Trạng thái phiếu
            </InputLabel>
            <Select
              value={filters.status}
              onChange={e => {
                setFilters({ ...filters, status: e.target.value, page: 1 });
              }}
            >
              <MenuItem value="">Tất cả</MenuItem>
              <MenuItem value="0">Chờ gán</MenuItem>
              <MenuItem value="1">Hoàn thành</MenuItem>
              <MenuItem value="-1">Đã hủy</MenuItem>
            </Select>
          </FormControl>

          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleFilterClose}
            style={{ textTransform: 'none', fontWeight: 600, borderRadius: 8 }}
          >
            Áp dụng
          </Button>
        </Popover>

        {hasActiveFilters && (
          <Tooltip title="Xóa tất cả bộ lọc">
            <IconButton
              size="small"
              onClick={handleReset}
              style={{
                color: '#D9042F',
                border: '1px solid #D9042F33',
                borderRadius: 8,
                padding: 8,
                height: 40,
                width: 40
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {canCreateTicket && (
          <Button
            variant="contained"
            className={classes.createBtn}
            onClick={() => setOpenCreateModal(true)}
          >
            + Tạo phiếu bảo hành
          </Button>
        )}
      </Card>

      {/* Main Table Card (Hỗ trợ kéo thả chuột nằm ngang & Sticky đến Serial lỗi) */}
      <Card className={classes.tableCard}>
        <Box
          ref={scrollContainerRef}
          onMouseDown={onMouseDown}
          onMouseLeave={onMouseLeave}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}
          onScroll={handleScroll}
          style={{
            cursor: isDown ? 'grabbing' : 'grab',
            overflowX: 'auto',
            overflowY: 'hidden',
            userSelect: isDown ? 'none' : 'auto',
            width: '100%'
          }}
        >
          <Table style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: 1500 }}>
            <TableHead>
              <TableRow>
                <StyledTableCell className={classes.stickyHeader} style={{ left: 0, width: 140, minWidth: 140 }}>
                  Thời gian
                </StyledTableCell>
                <StyledTableCell className={classes.stickyHeader} style={{ left: 140, width: 120, minWidth: 120 }}>
                  Mã phiếu
                </StyledTableCell>
                <StyledTableCell
                  className={classes.stickyHeader}
                  style={{
                    left: 260,
                    width: 140,
                    minWidth: 140,
                    borderRight: isScrolled ? '1px solid rgba(0,0,0,0.1)' : 'none',
                    boxShadow: isScrolled ? '4px 0 8px rgba(0,0,0,0.06)' : 'none',
                    transition: 'border-right 0.2s, box-shadow 0.2s'
                  }}
                >
                  Serial lỗi
                </StyledTableCell>
                <StyledTableCell style={{ minWidth: 150 }}>
                  Serial bảo hành
                </StyledTableCell>
                <StyledTableCell style={{ minWidth: 130 }}>Phương tiện</StyledTableCell>
                <StyledTableCell style={{ minWidth: 130 }}>Loại thiết bị</StyledTableCell>
                <StyledTableCell style={{ minWidth: 140 }}>Kho lỗi</StyledTableCell>
                <StyledTableCell style={{ minWidth: 140 }}>Kho bảo hành</StyledTableCell>
                <StyledTableCell style={{ minWidth: 130 }} align="center">Đồng bộ MQTT</StyledTableCell>
                <StyledTableCell style={{ minWidth: 110 }} align="center">Trạng thái</StyledTableCell>
                <StyledTableCell style={{ minWidth: 160 }}>Ghi chú</StyledTableCell>
                <StyledTableCell style={{ minWidth: 140 }}>Người tạo</StyledTableCell>
                <StyledTableCell style={{ minWidth: 90 }} align="center">Thao tác</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <StyledTableCell colSpan={13} align="center" style={{ padding: 40 }}>
                    <CircularProgress size={32} />
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                      Đang tải dữ liệu phiếu bảo hành...
                    </Typography>
                  </StyledTableCell>
                </TableRow>
              ) : tickets.length === 0 ? (
                <TableRow>
                  <StyledTableCell colSpan={13} align="center" style={{ padding: 40 }}>
                    <Typography variant="body1" color="textSecondary">
                      Không tìm thấy phiếu bảo hành nào
                    </Typography>
                  </StyledTableCell>
                </TableRow>
              ) : (
                tickets.map((row, index) => {
                  const isMqttOk = Number(row.mqtt_published) === 1;
                  return (
                    <TableRow key={row.id || index} className={classes.tableRow}>
                      <StyledTableCell className={classes.stickyCol1} style={{ left: 0, width: 140, minWidth: 140, whiteSpace: 'nowrap' }}>
                        {row.created_at ? moment(row.created_at).format('DD/MM/YYYY HH:mm') : '-'}
                      </StyledTableCell>
                      <StyledTableCell className={classes.stickyCol2} style={{ left: 140, width: 120, minWidth: 120, fontWeight: 700, color: '#D9042F' }}>
                        {row.ticket_code}
                      </StyledTableCell>
                      <StyledTableCell
                        className={classes.stickyCol3}
                        style={{
                          left: 260,
                          width: 140,
                          minWidth: 140,
                          borderRight: isScrolled ? '1px solid rgba(0,0,0,0.1)' : 'none',
                          boxShadow: isScrolled ? '4px 0 8px rgba(0,0,0,0.06)' : 'none',
                          transition: 'border-right 0.2s, box-shadow 0.2s'
                        }}
                      >
                        <Typography variant="body2" style={{ fontWeight: 600 }}>{row.faulty_serial}</Typography>
                        {row.faulty_imei && (
                          <Typography variant="caption" color="textSecondary" display="block">
                            IMEI: {row.faulty_imei}
                          </Typography>
                        )}
                      </StyledTableCell>
                      <StyledTableCell style={{ minWidth: 150 }}>
                        {row.replacement_serial ? (
                          <Box display="flex" alignItems="center" justifyContent="space-between" style={{ gap: 4 }}>
                            <Box>
                              <Typography variant="body2" style={{ fontWeight: 600 }}>{row.replacement_serial}</Typography>
                              {row.replacement_imei && (
                                <Typography variant="caption" color="textSecondary" display="block">
                                  IMEI: {row.replacement_imei}
                                </Typography>
                              )}
                            </Box>
                            {canEditOrDeleteTicket && (
                              <Tooltip title="Đổi / Swap serial bảo hành">
                                <IconButton size="small" onClick={() => handleOpenSwapModal(row)} style={{ padding: 4, color: '#D9042F' }}>
                                  <SwapHorizIcon style={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        ) : canEditOrDeleteTicket ? (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleOpenSwapModal(row)}
                            startIcon={<SwapHorizIcon style={{ fontSize: 15 }} />}
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: '#D9042F',
                              borderColor: '#D9042F',
                              borderRadius: 6,
                              padding: '2px 8px',
                              textTransform: 'none'
                            }}
                          >
                            Swap Serial
                          </Button>
                        ) : (
                          <Typography variant="body2" color="textSecondary">-</Typography>
                        )}
                      </StyledTableCell>
                      <StyledTableCell style={{ fontWeight: 600 }}>
                        {row.license_plate || '-'}
                      </StyledTableCell>
                      <StyledTableCell>{row.device_type_name || '-'}</StyledTableCell>
                      <StyledTableCell>{row.faulty_agency_name || '-'}</StyledTableCell>
                      <StyledTableCell>{row.replacement_agency_name || '-'}</StyledTableCell>
                      <StyledTableCell align="center">
                        <Tooltip title={isMqttOk ? 'Đã kích hoạt/đồng bộ MQTT' : 'Chưa đồng bộ MQTT'}>
                          <Chip
                            size="small"
                            label={isMqttOk ? 'Đã đồng bộ' : 'Chưa đồng bộ'}
                            className={isMqttOk ? classes.chipSuccess : classes.chipWarning}
                          />
                        </Tooltip>
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        <Chip
                          size="small"
                          label={
                            Number(row.status) === 1
                              ? 'Hoàn thành'
                              : Number(row.status) === 0
                              ? 'Chờ gán'
                              : 'Đã hủy'
                          }
                          className={
                            Number(row.status) === 1
                              ? classes.chipSuccess
                              : Number(row.status) === 0
                              ? classes.chipWarning
                              : classes.chipCancel
                          }
                        />
                      </StyledTableCell>
                      <StyledTableCell style={{ maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {row.note || '-'}
                      </StyledTableCell>
                      <StyledTableCell>
                        {(() => {
                          const { username, role } = getCreatorInfo(row, dataLogin, flatAgencies);
                          if (username === '-') {
                            return <Typography variant="body2" color="textSecondary">-</Typography>;
                          }
                          return (
                            <Box>
                              <Typography variant="body2" style={{ fontWeight: 600, color: '#1e293b', lineHeight: 1.3 }}>
                                {username}
                              </Typography>
                              {role && (
                                <Typography
                                  variant="caption"
                                  style={{
                                    color: '#64748b',
                                    display: 'block',
                                    fontSize: 11,
                                    fontWeight: 500,
                                    marginTop: 2,
                                    lineHeight: 1.2
                                  }}
                                >
                                  {role}
                                </Typography>
                              )}
                            </Box>
                          );
                        })()}
                      </StyledTableCell>
                      <StyledTableCell align="center">
                        {canEditOrDeleteTicket ? (
                          <IconButton
                            size="small"
                            onClick={e => handleActionMenuOpen(e, row)}
                            style={{ padding: 4 }}
                          >
                            <MoreVertIcon fontSize="small" style={{ color: '#64748b' }} />
                          </IconButton>
                        ) : (
                          <Typography variant="body2" color="textSecondary">-</Typography>
                        )}
                      </StyledTableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Phân trang chuẩn theo Quản trị bán hàng */}
        {total > 0 && (
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            style={{
              padding: '12px 16px',
              borderTop: '1px dashed #e0e0e0',
              gap: '15px'
            }}
          >
            <Typography style={{ fontSize: '14px', color: '#555' }}>
              Tổng: {total || 0}
            </Typography>
            <Pagination
              count={Math.ceil((total || 0) / (filters.page_size || 20)) || 1}
              color="primary"
              size="small"
              onChange={handlePageChange}
              page={filters.page || 1}
              siblingCount={1}
              boundaryCount={2}
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Card>

      {/* Modal create warranty ticket */}
      <CreateWarrantyTicketModal
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Modal swap serial for a ticket */}
      <SwapSerialModal
        open={openSwapModal}
        onClose={() => setOpenSwapModal(false)}
        onSuccess={handleCreateSuccess}
        ticket={selectedTicketForSwap}
      />

      {/* Menu 3 chấm thao tác */}
      <Menu
        anchorEl={actionAnchorEl}
        keepMounted
        open={Boolean(actionAnchorEl) && canEditOrDeleteTicket}
        onClose={handleActionMenuClose}
        PaperProps={{
          style: {
            minWidth: 170,
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
          }
        }}
      >
        <MenuItem
          onClick={() => {
            const ticket = selectedTicket;
            handleActionMenuClose();
            if (ticket) handleOpenSwapModal(ticket);
          }}
        >
          <ListItemIcon style={{ minWidth: 32, color: '#D9042F' }}>
            <SwapHorizIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Đổi / Swap Serial"
            primaryTypographyProps={{ style: { fontSize: 13, fontWeight: 600, color: '#1a1f36' } }}
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            const ticket = selectedTicket;
            handleActionMenuClose();
            if (ticket) handleOpenDeleteDialog(ticket);
          }}
          style={{ color: '#d32f2f' }}
        >
          <ListItemIcon style={{ minWidth: 32, color: '#d32f2f' }}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Xóa phiếu"
            primaryTypographyProps={{ style: { fontSize: 13, fontWeight: 500, color: '#d32f2f' } }}
          />
        </MenuItem>
      </Menu>

      {/* Dialog xác nhận xóa phiếu */}
      <Dialog
        open={openDeleteDialog}
        onClose={deleting ? undefined : () => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle style={{ fontWeight: 700 }}>Xác nhận xóa phiếu bảo hành</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ fontSize: 14, color: '#334155' }}>
            Bạn có chắc chắn muốn xóa phiếu bảo hành{' '}
            <strong>{selectedTicket?.ticket_code || selectedTicket?.id}</strong> không?
            Hành động này không thể hoàn tác.
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: '12px 24px' }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            disabled={deleting}
            style={{ textTransform: 'none', fontWeight: 600 }}
          >
            Hủy
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            disabled={deleting}
            style={{
              backgroundColor: '#d32f2f',
              color: '#fff',
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 6
            }}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Xóa phiếu
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

